// ===========================================
// Registration Page
// ===========================================
// Handles registration flow after M&G approval
// User arrives here from email link, validates token, and proceeds to payment
// Design matches MeetAndGreetBookingPage with piano header

import { useEffect, useState, useContext } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Paper,
  CircularProgress,
  Button,
  Alert,
  Divider,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Chip,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import PersonIcon from '@mui/icons-material/Person';
import ChildCareIcon from '@mui/icons-material/ChildCare';
import MusicNoteIcon from '@mui/icons-material/MusicNote';
import PaymentIcon from '@mui/icons-material/Payment';
import EmailIcon from '@mui/icons-material/Email';
import LockIcon from '@mui/icons-material/Lock';
import { useMutation, useQuery } from '@tanstack/react-query';
import { extractSchoolSlugFromPath } from '../../utils/routing';
import { registrationApi, RegistrationValidationData } from '../../api/registration.api';
import SchoolHomeButton from '../../components/public/SchoolHomeButton';
import { TenantContext } from '../../contexts/TenantContext';
import PublicFooter from '../../components/public/PublicFooter';
import { apiErrorCode } from '../../utils/apiError';

export default function RegistrationPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const schoolSlug = extractSchoolSlugFromPath(window.location.pathname);
  // The school's own name for the welcome line (TenantProvider wraps /:schoolSlug/register).
  const schoolName = useContext(TenantContext)?.school?.name;
  const token = searchParams.get('token');
  const mag = searchParams.get('mag');
  const sessionId = searchParams.get('session_id'); // Stripe redirect

  const [registrationComplete, setRegistrationComplete] = useState(false);

  // Validate registration token
  const validationQuery = useQuery({
    queryKey: ['registration-validate', token, mag],
    queryFn: () => registrationApi.validate(token!, mag!),
    enabled: Boolean(token && mag && !sessionId),
    retry: false,
  });

  // Create checkout session mutation
  const checkoutMutation = useMutation({
    mutationFn: (data: RegistrationValidationData) => {
      const baseUrl = schoolSlug ? `${window.location.origin}/${schoolSlug}/register` : `${window.location.origin}/register`;
      return registrationApi.createCheckoutSession({
        meetAndGreetId: data.meetAndGreetId,
        schoolId: data.schoolId,
        successUrl: `${baseUrl}?token=${token}&mag=${mag}`,
        cancelUrl: `${baseUrl}?token=${token}&mag=${mag}&cancelled=true`,
      });
    },
    onSuccess: (response) => {
      // Redirect to Stripe Checkout
      window.location.href = response.url;
    },
    onError: (error) => {
      // #430: the family turned out to be an existing one (the school's rule, not ours).
      // Re-ask validate so the page shows the "already with us" state instead of an error.
      if (apiErrorCode(error) === 'EXISTING_FAMILY') {
        void validationQuery.refetch();
      }
    },
  });

  // Track if we've already attempted completion
  const [completionAttempted, setCompletionAttempted] = useState(false);

  // Complete registration after payment
  const completeMutation = useMutation({
    mutationFn: (sessionId: string) => registrationApi.completeRegistration(sessionId),
    onSuccess: () => {
      setRegistrationComplete(true);
    },
    retry: false, // Don't retry on failure - prevents duplicate registration attempts
  });

  // Handle Stripe redirect (payment success)
  useEffect(() => {
    if (sessionId && !completionAttempted && !registrationComplete) {
      setCompletionAttempted(true);
      completeMutation.mutate(sessionId);
    }
  }, [sessionId, completionAttempted, registrationComplete, completeMutation]);

  // Handle proceed to payment
  const handleProceedToPayment = () => {
    if (validationQuery.data) {
      checkoutMutation.mutate(validationQuery.data);
    }
  };

  // Check for cancelled payment
  const wasCancelled = searchParams.get('cancelled') === 'true';

  // Page wrapper component with decorations
  const PageWrapper = ({ children, title }: { children: React.ReactNode; title: string }) => (
    <Box
      sx={{
        bgcolor: '#FCF6E6', // Cream brand color
        minHeight: '100vh',
        position: 'relative',
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      {/* Character Decorations - Left */}
      <Box
        component="img"
        src="/character.png"
        alt=""
        sx={{
          position: 'fixed',
          left: { xs: -60, md: -20, lg: 20 },
          bottom: { xs: 'calc(10% - 50px)', md: 'calc(20% - 50px)' },
          width: { xs: 120, md: 160, lg: 200 },
          opacity: 0.4,
          pointerEvents: 'none',
          transform: 'rotate(-10deg)',
          display: { xs: 'none', sm: 'block' },
        }}
      />

      {/* Character Decorations - Right */}
      <Box
        component="img"
        src="/character-mint.png"
        alt=""
        sx={{
          position: 'fixed',
          right: { xs: -60, md: -20, lg: 20 },
          bottom: { xs: '30%', md: '40%' },
          width: { xs: 100, md: 140, lg: 180 },
          opacity: 0.4,
          pointerEvents: 'none',
          transform: 'rotate(10deg)',
          display: { xs: 'none', sm: 'block' },
        }}
      />

      {/* Music Notes Decorations */}
      <Box
        sx={{
          position: 'fixed',
          left: { xs: 10, md: 40 },
          top: 'calc(30% + 50px)',
          width: { xs: 60, md: 80 },
          opacity: 0.3,
          pointerEvents: 'none',
          display: { xs: 'none', lg: 'block' },
        }}
      >
        <Box component="img" src="/music-notes.png" alt="" sx={{ width: '100%' }} />
      </Box>
      <Box
        sx={{
          position: 'fixed',
          right: { xs: 10, md: 40 },
          top: '50%',
          width: { xs: 60, md: 80 },
          opacity: 0.3,
          pointerEvents: 'none',
          display: { xs: 'none', lg: 'block' },
        }}
      >
        <Box component="img" src="/music-notes-3.png" alt="" sx={{ width: '100%' }} />
      </Box>

      {/* Mobile Header - Logo centered, no piano */}
      <Box
        sx={{
          display: { xs: 'flex', sm: 'none' },
          flexDirection: 'column',
          alignItems: 'center',
          pt: 3,
          pb: 2,
          mb: 2,
        }}
      >
        <Box
          component="img"
          src="/music-n-me-logo.png"
          alt="Music 'n Me"
          sx={{
            maxWidth: 120,
            height: 'auto',
            mb: 2,
          }}
        />
        <Typography
          variant="h4"
          sx={{
            fontFamily: '"Monkey Mayhem", "Comic Sans MS", cursive',
            color: 'primary.main',
            textAlign: 'center',
            fontSize: '1.75rem',
          }}
        >
          {title}
        </Typography>
      </Box>

      {/* Desktop Header - Piano with Logo Overlay */}
      <Box
        sx={{
          display: { xs: 'none', sm: 'block' },
          position: 'relative',
          width: '100%',
          mb: 3,
        }}
      >
        {/* Piano Background - full height */}
        <Box
          component="img"
          src="/piano-header.png"
          alt="Piano keys"
          sx={{
            width: '100%',
            height: 'auto',
            display: 'block',
          }}
        />
        {/* Logo - top left */}
        <Box
          component="img"
          src="/music-n-me-logo.png"
          alt="Music 'n Me"
          sx={{
            position: 'absolute',
            top: { sm: 12, md: 16 },
            left: { sm: 12, md: 16 },
            maxWidth: { sm: 100, md: 120 },
            height: 'auto',
          }}
        />
        {/* Title overlay - bottom */}
        <Typography
          variant="h3"
          sx={{
            position: 'absolute',
            bottom: 5,
            left: 0,
            right: 0,
            fontFamily: '"Monkey Mayhem", "Comic Sans MS", cursive',
            color: 'white',
            textAlign: 'center',
            fontSize: { sm: '2rem', md: '2.5rem' },
            textShadow: '2px 2px 4px rgba(0,0,0,0.5)',
          }}
        >
          {title}
        </Typography>
      </Box>

      <Box sx={{ flex: 1 }}>
        {children}
      </Box>
      <PublicFooter />
    </Box>
  );

  // Loading/processing state for Stripe redirect (session_id present)
  // This must come BEFORE validation error check since validation is disabled when session_id exists
  if (sessionId && !registrationComplete && !completeMutation.isError) {
    return (
      <PageWrapper title="Complete Registration">
        <Container maxWidth="sm" sx={{ py: 4, textAlign: 'center', position: 'relative', zIndex: 1 }}>
          <Paper sx={{ p: 4, bgcolor: 'background.paper' }}>
            <CircularProgress size={60} />
            <Typography variant="h6" sx={{ mt: 3 }}>
              Completing your registration...
            </Typography>
            <Typography color="text.secondary" sx={{ mt: 1 }}>
              Please wait, this may take a moment.
            </Typography>
          </Paper>
        </Container>
      </PageWrapper>
    );
  }

  // Loading state - validating token
  if (validationQuery.isLoading) {
    return (
      <PageWrapper title="Complete Registration">
        <Container maxWidth="sm" sx={{ py: 4, textAlign: 'center', position: 'relative', zIndex: 1 }}>
          <Paper sx={{ p: 4, bgcolor: 'background.paper' }}>
            <CircularProgress size={60} />
            <Typography variant="h6" sx={{ mt: 3 }}>
              Validating registration link...
            </Typography>
          </Paper>
        </Container>
      </PageWrapper>
    );
  }

  // Registration complete state
  if (registrationComplete && completeMutation.isSuccess) {
    return (
      <PageWrapper title="Welcome!">
        <Container maxWidth="sm" sx={{ py: 4, position: 'relative', zIndex: 1 }}>
          <Paper sx={{ p: 4, textAlign: 'center', bgcolor: 'background.paper' }}>
            <CheckCircleIcon sx={{ fontSize: 80, color: 'success.main', mb: 2 }} />
            <Typography
              variant="h4"
              sx={{
                fontFamily: '"Monkey Mayhem", "Comic Sans MS", cursive',
                color: 'primary.main',
                mb: 2,
              }}
            >
              Registration Complete!
            </Typography>
            <Typography variant="h6" gutterBottom>
              Welcome to the {schoolName ?? "Music 'n Me"} family!
            </Typography>

            <Alert severity="success" sx={{ my: 3, textAlign: 'left' }}>
              <Typography variant="body2">
                Your account has been created. Check your email for a link to set your password (it works for 7 days).
              </Typography>
            </Alert>

            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', mt: 3 }}>
              <Button
                variant="contained"
                size="large"
                onClick={() => navigate(schoolSlug ? `/${schoolSlug}/login` : '/login')}
              >
                Go to Login
              </Button>
              <SchoolHomeButton variant="outlined" size="large" />
            </Box>
          </Paper>
        </Container>
      </PageWrapper>
    );
  }

  // Registration completion error
  if (completeMutation.isError) {
    const errorMessage =
      (completeMutation.error as Error & { response?: { data?: { message?: string } } })?.response?.data?.message ||
      'Failed to complete registration. Please contact support.';

    // #430: switch on the server's code, never on message text (an uncoded
    // message that happens to contain "already" is an error, not a success).
    const errorCode = apiErrorCode(completeMutation.error);
    const isAlreadyCompleted = errorCode === 'REGISTRATION_ALREADY_COMPLETE';

    if (errorCode === 'REGISTRATION_NEEDS_OFFICE') {
      return (
        <PageWrapper title="Payment Received">
          <Container maxWidth="sm" sx={{ py: 4, position: 'relative', zIndex: 1 }}>
            <Paper sx={{ p: 4, textAlign: 'center', bgcolor: 'background.paper' }}>
              <PaymentIcon sx={{ fontSize: 80, color: 'primary.main', mb: 2 }} />
              <Typography
                variant="h4"
                sx={{
                  fontFamily: '"Monkey Mayhem", "Comic Sans MS", cursive',
                  color: 'primary.main',
                  mb: 2,
                }}
              >
                Payment received
              </Typography>
              <Alert severity="info" sx={{ mb: 3, textAlign: 'left' }}>
                <Typography variant="body2">{errorMessage}</Typography>
              </Alert>
              <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', mt: 3 }}>
                <Button
                  variant="contained"
                  size="large"
                  onClick={() => navigate(schoolSlug ? `/${schoolSlug}/login` : '/login')}
                >
                  Go to Login
                </Button>
                <SchoolHomeButton variant="outlined" size="large" />
              </Box>
            </Paper>
          </Container>
        </PageWrapper>
      );
    }

    if (isAlreadyCompleted) {
      return (
        <PageWrapper title="Registration Complete">
          <Container maxWidth="sm" sx={{ py: 4, position: 'relative', zIndex: 1 }}>
            <Paper sx={{ p: 4, textAlign: 'center', bgcolor: 'background.paper' }}>
              <CheckCircleIcon sx={{ fontSize: 80, color: 'success.main', mb: 2 }} />
              <Typography
                variant="h4"
                sx={{
                  fontFamily: '"Monkey Mayhem", "Comic Sans MS", cursive',
                  color: 'primary.main',
                  mb: 2,
                }}
              >
                Already Registered!
              </Typography>
              <Alert severity="info" sx={{ mb: 3, textAlign: 'left' }}>
                <Typography variant="body2">
                  {errorMessage}
                </Typography>
              </Alert>
              <Typography color="text.secondary" sx={{ mb: 3 }}>
                Your registration has already been completed. Check your email inbox (and spam folder)
                for the email with your set-password link. If the link has expired, use Forgot
                password on the login page.
              </Typography>
              <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', mt: 3 }}>
                <Button
                  variant="contained"
                  size="large"
                  onClick={() => navigate(schoolSlug ? `/${schoolSlug}/login` : '/login')}
                >
                  Go to Login
                </Button>
                <SchoolHomeButton variant="outlined" size="large" />
              </Box>
            </Paper>
          </Container>
        </PageWrapper>
      );
    }

    return (
      <PageWrapper title="Registration Error">
        <Container maxWidth="sm" sx={{ py: 4, position: 'relative', zIndex: 1 }}>
          <Paper sx={{ p: 4, textAlign: 'center', bgcolor: 'background.paper' }}>
            <ErrorIcon sx={{ fontSize: 80, color: 'error.main', mb: 2 }} />
            <Typography variant="h4" gutterBottom>
              Registration Error
            </Typography>
            <Alert severity="error" sx={{ mb: 3 }}>
              {errorMessage}
            </Alert>
            <Typography color="text.secondary" sx={{ mb: 3 }}>
              Your payment was successful, but we encountered an issue completing your registration.
              Please contact the school for assistance.
            </Typography>
            <SchoolHomeButton variant="contained" />
          </Paper>
        </Container>
      </PageWrapper>
    );
  }

  // Validation error state
  if (validationQuery.isError) {
    const errorMessage =
      (validationQuery.error as Error & { response?: { data?: { message?: string } } })?.response?.data?.message ||
      'Invalid or expired registration link.';

    return (
      <PageWrapper title="Invalid Link">
        <Container maxWidth="sm" sx={{ py: 4, position: 'relative', zIndex: 1 }}>
          <Paper sx={{ p: 4, textAlign: 'center', bgcolor: 'background.paper' }}>
            <ErrorIcon sx={{ fontSize: 80, color: 'error.main', mb: 2 }} />
            <Typography variant="h4" gutterBottom>
              Invalid Registration Link
            </Typography>
            <Alert severity="error" sx={{ mb: 3 }}>
              {errorMessage}
            </Alert>
            {/* The school can now genuinely issue a new link, so this tells the
                parent what to ask for rather than the vaguer "contact us". */}
            <Typography color="text.secondary" sx={{ mb: 3 }}>
              This registration link has expired or has already been used.
              <br />
              <strong>Ask the school to resend your registration link</strong> — they can
              send a fresh one straight to your inbox.
            </Typography>
            <SchoolHomeButton variant="contained" />
          </Paper>
        </Container>
      </PageWrapper>
    );
  }

  // No token/mag provided
  if (!token || !mag) {
    return (
      <PageWrapper title="Registration">
        <Container maxWidth="sm" sx={{ py: 4, position: 'relative', zIndex: 1 }}>
          <Paper sx={{ p: 4, bgcolor: 'background.paper' }}>
            <Alert severity="warning">
              No registration token provided. Please use the link from your email.
            </Alert>
            <SchoolHomeButton sx={{ mt: 2 }} variant="contained" />
          </Paper>
        </Container>
      </PageWrapper>
    );
  }

  // Valid token - show registration details and payment button
  const data = validationQuery.data;

  if (!data) {
    return null;
  }

  // #430: the family already has an account. No fee, no pay button.
  if (data.existingFamily) {
    return (
      <PageWrapper title="Already With Us">
        <Container maxWidth="sm" sx={{ py: 4, position: 'relative', zIndex: 1 }}>
          <Paper sx={{ p: 4, textAlign: 'center', bgcolor: 'background.paper' }}>
            <Typography
              variant="h4"
              sx={{
                fontFamily: '"Monkey Mayhem", "Comic Sans MS", cursive',
                color: 'primary.main',
                mb: 2,
              }}
            >
              Already with us
            </Typography>
            <Alert severity="info" sx={{ mb: 3, textAlign: 'left' }}>
              <Typography variant="body2">
                Your family already has an account with {data.schoolName}. The school will add{' '}
                {data.studentName} to your family. You don't need to pay a registration fee.
              </Typography>
              {data.paymentReceived && (
                <Typography variant="body2" sx={{ mt: 1 }}>
                  We've received your payment; the school will be in touch about it.
                </Typography>
              )}
            </Alert>
            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', mt: 3 }}>
              <Button
                variant="contained"
                size="large"
                onClick={() => navigate(schoolSlug ? `/${schoolSlug}/login` : '/login')}
              >
                Go to Login
              </Button>
              <SchoolHomeButton variant="outlined" size="large" />
            </Box>
          </Paper>
        </Container>
      </PageWrapper>
    );
  }

  return (
    <PageWrapper title="Complete Registration">
      {/* Subtitle */}
      <Container maxWidth="md" sx={{ mb: 3, position: 'relative', zIndex: 1 }}>
        <Typography
          variant="h6"
          sx={{
            textAlign: 'center',
            color: 'text.secondary',
          }}
        >
          {data.schoolName} - One step away from starting your musical journey!
        </Typography>
      </Container>

      <Container maxWidth="sm" sx={{ pb: 4, position: 'relative', zIndex: 1 }}>
        <Paper sx={{ p: 3, bgcolor: 'background.paper' }}>
          {wasCancelled && (
            <Alert severity="info" sx={{ mb: 3 }}>
              Payment was cancelled. You can try again when you're ready.
            </Alert>
          )}

          <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
            Registration Details
          </Typography>

          <Paper
            variant="outlined"
            sx={{
              p: 2,
              mb: 3,
              bgcolor: 'rgba(255, 255, 255, 0.5)',
              borderColor: 'divider',
              borderRight: 4,
              borderRightColor: '#FFCE00', // Brand yellow
            }}
          >
            <List dense disablePadding>
              <ListItem disableGutters>
                <ListItemIcon sx={{ minWidth: 40 }}>
                  <ChildCareIcon color="primary" />
                </ListItemIcon>
                <ListItemText
                  primary="Student"
                  secondary={`${data.studentName} (${data.studentAge} years old)`}
                  primaryTypographyProps={{ variant: 'body2', color: 'text.secondary' }}
                  secondaryTypographyProps={{ variant: 'body1', fontWeight: 500, color: 'text.primary' }}
                />
              </ListItem>
              <ListItem disableGutters>
                <ListItemIcon sx={{ minWidth: 40 }}>
                  <PersonIcon color="primary" />
                </ListItemIcon>
                <ListItemText
                  primary="Parent/Guardian"
                  secondary={data.parentName}
                  primaryTypographyProps={{ variant: 'body2', color: 'text.secondary' }}
                  secondaryTypographyProps={{ variant: 'body1', fontWeight: 500, color: 'text.primary' }}
                />
              </ListItem>
              {data.instrumentName && (
                <ListItem disableGutters>
                  <ListItemIcon sx={{ minWidth: 40 }}>
                    <MusicNoteIcon color="primary" />
                  </ListItemIcon>
                  <ListItemText
                    primary="Instrument Interest"
                    secondary={data.instrumentName}
                    primaryTypographyProps={{ variant: 'body2', color: 'text.secondary' }}
                    secondaryTypographyProps={{ variant: 'body1', fontWeight: 500, color: 'text.primary' }}
                  />
                </ListItem>
              )}
            </List>
          </Paper>

          <Divider sx={{ my: 3 }} />

          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Registration Fee
            </Typography>
            <Chip
              label={`$${data.registrationFee.toFixed(2)} AUD`}
              color="primary"
              size="medium"
              sx={{ fontSize: '1.1rem', py: 2, px: 1 }}
            />
          </Box>

          {/* What to Expect */}
          <Paper
            variant="outlined"
            sx={{
              p: 2,
              mb: 3,
              bgcolor: 'rgba(255, 255, 255, 0.5)',
              borderLeft: 4,
              borderLeftColor: '#96DAC9', // Brand mint/green
            }}
          >
            <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
              What Happens Next
            </Typography>
            <List dense disablePadding>
              <ListItem disableGutters>
                <ListItemIcon sx={{ minWidth: 36 }}>
                  <PaymentIcon fontSize="small" color="primary" />
                </ListItemIcon>
                <ListItemText primary="Complete your secure payment via Stripe" />
              </ListItem>
              <ListItem disableGutters>
                <ListItemIcon sx={{ minWidth: 36 }}>
                  <EmailIcon fontSize="small" color="primary" />
                </ListItemIcon>
                <ListItemText primary="Set your password from the email we send you" />
              </ListItem>
              <ListItem disableGutters>
                <ListItemIcon sx={{ minWidth: 36 }}>
                  <LockIcon fontSize="small" color="primary" />
                </ListItemIcon>
                <ListItemText primary="Log in to your account" />
              </ListItem>
              <ListItem disableGutters>
                <ListItemIcon sx={{ minWidth: 36 }}>
                  <CheckCircleIcon fontSize="small" color="primary" />
                </ListItemIcon>
                <ListItemText primary="Access your family portal and enrol in lessons" />
              </ListItem>
            </List>
          </Paper>

          <Button
            variant="contained"
            size="large"
            fullWidth
            startIcon={<PaymentIcon />}
            onClick={handleProceedToPayment}
            disabled={checkoutMutation.isPending}
            sx={{ py: 1.5 }}
          >
            {checkoutMutation.isPending ? (
              <>
                <CircularProgress size={20} sx={{ mr: 1 }} color="inherit" />
                Preparing Payment...
              </>
            ) : (
              `Pay $${data.registrationFee.toFixed(2)} & Complete Registration`
            )}
          </Button>

          {checkoutMutation.isError && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {(checkoutMutation.error as Error & { response?: { data?: { message?: string } } })?.response?.data?.message ||
                'Failed to create checkout session. Please try again.'}
            </Alert>
          )}

          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ display: 'block', textAlign: 'center', mt: 2 }}
          >
            Secure payment powered by Stripe
          </Typography>

          <Divider sx={{ my: 3 }} />

          <Box sx={{ display: 'flex', justifyContent: 'center' }}>
            <Button variant="outlined" onClick={() => navigate(-1)}>
              ← Go Back
            </Button>
          </Box>
        </Paper>
      </Container>
    </PageWrapper>
  );
}
